import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Drawer
 * 
 * Componente de panel lateral retractable desde la derecha.
 * Ideal para formularios contextuales y detalles de items.
 */
const Drawer = ({
    isOpen,
    onClose,
    children,
    title,
    subtitle,
    icon: Icon,
    width = 'max-w-md', // md: 448px, xl: 576px
    variant = 'light' // 'light' | 'dark'
}) => {
    const isDark = variant === 'dark';

    // Bloqueo de scroll y cierre con Escape
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';

            const handleEsc = (e) => {
                if (e.key === 'Escape') onClose();
            };
            document.addEventListener('keydown', handleEsc);
            return () => {
                document.body.style.overflow = 'unset';
                document.removeEventListener('keydown', handleEsc);
            };
        }
    }, [isOpen, onClose]);

    return createPortal(
        <LazyMotion features={domAnimation}>
            <AnimatePresence>
                {isOpen && (
                    <div className="fixed inset-0 z-[100] flex justify-end isolate">
                        {/* Fondo desenfocado (Backdrop) */}
                        <m.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`absolute inset-0 backdrop-blur-sm cursor-pointer ${isDark ? 'bg-black/60' : 'bg-primary/20'}`}
                            onClick={onClose}
                            aria-hidden="true"
                        />

                        {/* Contenido del Panel */}
                        <m.div
                            initial={{ x: '100%', opacity: 0.5 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: '100%', opacity: 0.5 }}
                            transition={{ type: "spring", damping: 28, stiffness: 220, mass: 0.9 }}
                            className={`relative h-full w-full ${width} shadow-[-32px_0_64px_-16px_rgba(0,0,0,0.15)] flex flex-col border-l overflow-hidden ring-1 ring-black/5 ${isDark ? 'bg-[#0F172A] border-white/5 text-white' : 'bg-white border-gray-100'}`}
                            onClick={(e) => e.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                        >
                            {/* Cabecera (Header) */}
                            <div className={`px-8 py-7 border-b flex items-center justify-between backdrop-blur-xl sticky top-0 z-10 transition-all ${isDark ? 'bg-[#0F172A]/80 border-white/5' : 'bg-white/80 border-gray-50'}`}>
                                <div className="flex items-center gap-4">
                                    {Icon && (
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-inner ring-1 ${isDark ? 'bg-white/5 text-accent ring-white/10' : 'bg-gray-50 text-primary ring-gray-100/50'}`}>
                                            <Icon size={24} strokeWidth={2.5} />
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <h2 className={`text-xl font-black leading-none tracking-tight ${isDark ? 'text-white' : 'text-primary'}`}>{title}</h2>
                                        {subtitle && (
                                            <p className={`text-[10px] font-black uppercase tracking-[0.2em] mt-2 flex items-center gap-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-accent shadow-[0_0_10px_rgba(255,184,0,0.5)]' : 'bg-accent'} animate-pulse`} />
                                                {subtitle}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className={`p-3 rounded-2xl transition-all active:scale-90 group ${isDark ? 'bg-white/5 text-white/40 hover:text-white hover:bg-white/10' : 'bg-gray-50 text-gray-400 hover:text-primary hover:bg-gray-100'}`}
                                    aria-label="Cerrar panel"
                                >
                                    <X size={20} strokeWidth={2.5} className="group-hover:rotate-90 transition-transform duration-300" />
                                </button>
                            </div>

                            {/* Área Principal de Contenido (Scrollable) */}
                            <div className={`flex-1 overflow-y-auto px-8 py-10 overscroll-contain scrollbar-thin ${isDark ? 'scrollbar-thumb-white/10' : 'scrollbar-thumb-gray-200'}`}>
                                <div className="max-w-full">
                                    {children}
                                </div>
                            </div>
                        </m.div>
                    </div>
                )}
            </AnimatePresence>
        </LazyMotion>,
        document.body
    );
};

export default Drawer;
